import { computed, ref } from 'vue'
import axios from 'axios'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useConsentStore } from '@/stores/consent'
import { getApiErrorMessage } from '@/utils/error-handler'
import {
  getCurrentOfferingPackages,
  eligibleFreeTrialDaysFromPackage,
  getRevenueCatPurchaseErrorMessage,
  initializeRevenueCat,
  isRevenueCatPurchaseUserCancelled,
  loginRevenueCatUser,
  purchaseRevenueCatPackage,
  purchaseStartedFreeTrial,
} from '@/services/revenuecat'
import { gaDimensionsFromStoredUtm } from '@/utils/utm'
import { trackTrialStartedIfApplicable } from '@/utils/trialAnalytics'
import {
  currentWebPricing,
  loadWebPricing,
  setNativeTrialDays,
  usePricing,
} from '@/composables/usePricing'
import {
  STANDARD_PRICE_TIER,
  foundingSavings,
  webCheckoutPlan,
  webPriceTier,
} from '@/utils/webPricing'

function pricingFingerprint (pricing) {
  return pricing ? JSON.stringify([
    pricing.currency, pricing.monthly, pricing.annual, pricing.trial_days,
    pricing.founding_offer?.active, pricing.founding_offer?.prices,
  ]) : ''
}

export async function currentWebPriceTier () {
  await loadWebPricing()
  return webPriceTier(currentWebPricing())
}

/**
 * Plan-selection + purchase orchestration shared by the /upgrade page and the
 * inline upgrade modal. `source` identifies the calling surface for analytics
 * (e.g. 'upgrade_page', 'planner_locked_days_modal') — see docs/ga4-events.md.
 * @param {{ source: string }} options
 */
export function useUpgradeCheckout ({ source }) {
  const auth = useAuthStore()
  const consentStore = useConsentStore()
  const { t, locale } = useI18n()

  const checkoutLoading = ref(false)
  const errorMessage = ref('')
  const syncPending = ref(false)
  const syncRetryLoading = ref(false)
  const purchaseSuccessVisible = ref(false)
  const purchaseSuccessIsTrial = ref(false)
  const nativePurchaseWasTrial = ref(false)
  // Monthly, not annual. The smaller first commitment is the easier yes when
  // the point of the offer is to start a trial, and pre-selecting the year
  // beside a per-month figure was half of why the card read as a trick. The
  // audit asked for this to be treated as an experiment rather than assumed;
  // it is a deliberate product decision, and B3 measures it once traffic
  // exists rather than gating the change on that.
  const selectedPlan = ref('monthly')

  const nativePackages = ref({ monthly: null, annual: null })
  const nativePricingError = ref('')
  const nativePricingRetryLoading = ref(false)

  const isNativeMobile = computed(() => Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform()))
  const isIosPlatform = computed(() => Capacitor.getPlatform() === 'ios')

  const {
    pricing: webPricing,
    webLoading: webPricingLoading,
    webFailed: webPricingFailed,
    trialDays,
    trialMode,
    hasTrial,
    ctaLabel: pricingCtaLabel,
    nativeTrialDaysFor,
    status: pricingStatus,
    retry: retryWebPricing,
  } = usePricing({ plan: selectedPlan })

  const webPricingReady = computed(() => webPricing.value !== null)

  const webFoundingOfferActive = computed(() => (
    !isNativeMobile.value && webPricing.value?.founding_offer?.active === true
  ))

  const foundingSeatsLeft = computed(() => (
    webFoundingOfferActive.value ? webPricing.value.founding_offer.seats_left : null
  ))

  const foundingSeatCap = computed(() => (
    webFoundingOfferActive.value ? webPricing.value.founding_offer.seat_cap : null
  ))

  const effectiveWebPricing = computed(() => {
    if (!webPricing.value) return null
    return webFoundingOfferActive.value
      ? webPricing.value.founding_offer.prices
      : webPricing.value
  })

  const selectedPriceTier = computed(() => (
    isNativeMobile.value ? STANDARD_PRICE_TIER : webPriceTier(webPricing.value)
  ))

  function formatWebAmount (minorUnits, currency = effectiveWebPricing.value?.currency) {
    if (!currency || !Number.isFinite(minorUnits)) return ''
    try {
      return new Intl.NumberFormat(locale.value, { style: 'currency', currency })
        .format(minorUnits / 100)
    } catch {
      return `${(minorUnits / 100).toFixed(2)} ${currency}`
    }
  }

  function formatWholeAmount (minorUnits, currency) {
    const whole = Math.floor(minorUnits / 100)
    try {
      return new Intl.NumberFormat(locale.value, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(whole)
    } catch {
      return `${whole} ${currency}`
    }
  }

  function webPriceLine (key, minorUnits) {
    return effectiveWebPricing.value
      ? t(key, { price: formatWebAmount(minorUnits) })
      : ''
  }

  const webMonthlyPriceDisplay = computed(() => webPriceLine(
    'planner.upgrade_page_web_price_monthly',
    effectiveWebPricing.value?.monthly,
  ))

  const webAnnualPriceDisplay = computed(() => webPriceLine(
    'planner.upgrade_page_web_price_annual',
    effectiveWebPricing.value?.annual,
  ))

  const webAnnualBilledDisplay = computed(() => webPriceLine(
    'planner.upgrade_page_annual_billed',
    effectiveWebPricing.value?.annual,
  ))

  const webAnnualPerMonthDisplay = computed(() => webPriceLine(
    'planner.upgrade_page_annual_per_month',
    effectiveWebPricing.value?.annual_monthly_equivalent,
  ))

  const webStandardMonthlyPriceDisplay = computed(() => (
    webPricing.value ? formatWebAmount(webPricing.value.monthly, webPricing.value.currency) : ''
  ))

  const webStandardAnnualPriceDisplay = computed(() => (
    webPricing.value ? formatWebAmount(webPricing.value.annual, webPricing.value.currency) : ''
  ))

  const foundingSavingsInfo = computed(() => (
    webFoundingOfferActive.value ? foundingSavings(webPricing.value) : null
  ))

  // Founding: the saving against the standard price the backend reports, so the
  // struck-through reference and the claim cannot drift apart. Standard: twelve
  // months at the monthly rate less the annual price, rounded down so the badge
  // never promises more than the maths gives. Empty when there is nothing to
  // save, so the badge is hidden rather than promising a saving of zero.
  const webAnnualSavingDisplay = computed(() => {
    if (foundingSavingsInfo.value) {
      return t('planner.founding_saving_annual', {
        amount: formatWholeAmount(foundingSavingsInfo.value.annualAmount, webPricing.value.currency),
      })
    }
    const pricing = effectiveWebPricing.value
    if (!pricing) return ''
    const saving = (pricing.monthly * 12) - pricing.annual
    if (!(saving > 0)) return ''
    return t('planner.upgrade.price.annual_saving', {
      price: formatWholeAmount(saving, pricing.currency),
    })
  })

  const webMonthlySavingDisplay = computed(() => (
    foundingSavingsInfo.value
      ? t('planner.founding_saving_percent', { percent: foundingSavingsInfo.value.monthlyPercent })
      : ''
  ))

  const webSelectedPlanPriceLabel = computed(() => (
    selectedPlan.value === 'annual'
      ? webAnnualPriceDisplay.value
      : webMonthlyPriceDisplay.value
  ))

  function trialPriceLabelForPlan (plan) {
    if (isNativeMobile.value) {
      const pkg = plan === 'annual' ? nativePackages.value.annual : nativePackages.value.monthly
      const priceString = pkg?.product?.priceString?.trim()
      if (priceString) {
        return plan === 'annual'
          ? t('planner.upgrade_page_store_price_annual', { price: priceString })
          : t('planner.upgrade_page_store_price_monthly', { price: priceString })
      }
    }
    return plan === 'annual'
      ? webAnnualPriceDisplay.value
      : webMonthlyPriceDisplay.value
  }

  // Native stores attach the introductory offer to each package, so the monthly
  // and annual trial lengths can differ. The web trial is one setting for both.
  function trialDaysForPlan (plan) {
    return isNativeMobile.value ? nativeTrialDaysFor(plan) : trialDays.value
  }

  function trialLineForPlan (plan) {
    const days = trialDaysForPlan(plan)
    if (!(days > 0)) return ''
    const price = trialPriceLabelForPlan(plan)
    return price ? t('planner.upgrade_page_trial_line', { days, n: days, price }) : ''
  }

  const annualTrialLine = computed(() => trialLineForPlan('annual'))
  const monthlyTrialLine = computed(() => trialLineForPlan('monthly'))

  const showNativeTrialNote = computed(() => isNativeMobile.value && hasTrial.value)

  const purchaseSuccessMessage = computed(() => {
    if (purchaseSuccessIsTrial.value || auth.user?.subscription_status === 'trialing') {
      return isNativeMobile.value && hasTrial.value
        ? t('planner.upgrade_page_purchase_success_trial', { trial_days: trialDays.value, n: trialDays.value })
        : t('planner.upgrade_page_purchase_success_trial_generic')
    }
    return t('planner.upgrade_page_purchase_success')
  })

  const firstName = computed(() => {
    const name = auth.user?.name
    return typeof name === 'string' && name.trim() ? name.trim().split(' ')[0] : ''
  })

  const purchaseSuccessTitle = computed(() => (
    firstName.value
      ? t('planner.upgrade_page_purchase_success_title', { name: firstName.value })
      : t('planner.upgrade_page_purchase_success_title_generic')
  ))

  const nativePlansReady = computed(() => {
    if (!isNativeMobile.value) return true
    return !!(nativePackages.value.monthly && nativePackages.value.annual)
  })

  const nativeMonthlyPriceDisplay = computed(() => {
    const s = nativePackages.value.monthly?.product?.priceString?.trim()
    return s ? t('planner.upgrade_page_store_price_monthly', { price: s }) : ''
  })

  const nativeAnnualPriceDisplay = computed(() => {
    const s = nativePackages.value.annual?.product?.priceString?.trim()
    return s ? t('planner.upgrade_page_store_price_annual', { price: s }) : ''
  })

  const nativeAnnualMonthlyEquivalentDisplay = computed(() => {
    const product = nativePackages.value.annual?.product
    const price = product?.pricePerMonthString?.trim()
    return price ? t('planner.upgrade_page_store_annual_per_month', { price }) : ''
  })

  const nativeSavingsPercent = computed(() => {
    const annualPrice = nativePackages.value.annual?.product?.price
    const monthlyPrice = nativePackages.value.monthly?.product?.price
    if (!annualPrice || !monthlyPrice || monthlyPrice <= 0) return null
    const savings = Math.round((1 - (annualPrice / 12) / monthlyPrice) * 100)
    return savings > 0 ? savings : null
  })

  const checkoutDisabled = computed(() => {
    if (checkoutLoading.value) return true
    if (isNativeMobile.value) {
      if (nativePricingError.value) return true
      if (!nativePlansReady.value) return true
    }
    if (!isNativeMobile.value && !webPricingReady.value) return true
    return false
  })

  const webDisclosure = computed(() => {
    if (!webPricingReady.value) return ''
    const price = webSelectedPlanPriceLabel.value
    return hasTrial.value
      ? t('planner.upgrade_page_trial_disclosure_web', { price, trial_days: trialDays.value, n: trialDays.value })
      : t('planner.upgrade_page_disclosure_web_no_trial', { price })
  })

  const showWebPricingSkeleton = computed(() => !isNativeMobile.value && webPricingLoading.value)
  const showWebPricingError = computed(() => !isNativeMobile.value && webPricingFailed.value)

  const ctaLabel = computed(() => {
    if (checkoutLoading.value) return t('planner.upgrade_page_primary_cta_loading')
    return pricingCtaLabel.value
  })

  /**
   * @returns {Promise<{ ok: boolean, isPremium: boolean, axiosError: unknown }>}
   */
  async function syncPremiumWithBackend () {
    if (!auth.user?.id) {
      return { ok: false, isPremium: false, axiosError: null }
    }
    try {
      const { data } = await axios.post('/api/revenuecat/sync', {
        app_user_id: String(auth.user.id),
      })
      await auth.fetchUser()
      const isPremium = data?.is_premium === true || !!auth.user?.is_premium
      return { ok: true, isPremium, axiosError: null }
    } catch (axiosError) {
      return { ok: false, isPremium: false, axiosError }
    }
  }

  function applySyncResult (result) {
    if (result.isPremium) {
      const startedTrial = auth.user?.subscription_status === 'trialing'
        || (isNativeMobile.value && nativePurchaseWasTrial.value)
      purchaseSuccessIsTrial.value = startedTrial
      purchaseSuccessVisible.value = true
      syncPending.value = false
      errorMessage.value = ''
      void trackTrialStartedIfApplicable(consentStore, auth.user, {
        assume_trial: startedTrial,
        plan: selectedPlan.value,
        trial_days: trialDays.value,
        platform: isNativeMobile.value ? Capacitor.getPlatform() : 'web',
        checkout_provider: isNativeMobile.value ? 'revenuecat' : 'stripe',
        ...gaDimensionsFromStoredUtm(),
      })
      return
    }
    syncPending.value = true
    purchaseSuccessVisible.value = false
    if (result.ok && !result.isPremium) {
      errorMessage.value = t('planner.upgrade_page_sync_premium_not_ready')
      return
    }
    if (result.axiosError) {
      errorMessage.value = getApiErrorMessage(
        result.axiosError,
        t,
        'planner.upgrade_page_sync_after_purchase_error',
      )
    } else {
      errorMessage.value = t('planner.upgrade_page_sync_after_purchase_error')
    }
  }

  async function retryRevenueCatSync () {
    if (!auth.user?.id) return
    syncRetryLoading.value = true
    errorMessage.value = ''
    try {
      const result = await syncPremiumWithBackend()
      applySyncResult(result)
    } finally {
      syncRetryLoading.value = false
    }
  }

  async function loadNativeOfferings ({ fresh = false } = {}) {
    nativePricingError.value = ''
    nativePackages.value = { monthly: null, annual: null }
    setNativeTrialDays()
    try {
      await initializeRevenueCat()
      const pkgs = await getCurrentOfferingPackages({ fresh })
      nativePackages.value = pkgs
      setNativeTrialDays({
        monthly: eligibleFreeTrialDaysFromPackage(pkgs.monthly, pkgs.introEligibility.monthly) ?? 0,
        annual: eligibleFreeTrialDaysFromPackage(pkgs.annual, pkgs.introEligibility.annual) ?? 0,
      })
    } catch (err) {
      nativePricingError.value = getRevenueCatPurchaseErrorMessage(err, t)
        || t('planner.upgrade_page_native_pricing_error')
    }
  }

  async function retryLoadNativeOfferings () {
    nativePricingRetryLoading.value = true
    try {
      await loadNativeOfferings({ fresh: true })
    } finally {
      nativePricingRetryLoading.value = false
    }
  }

  const upgradeTermsUrl = computed(() => {
    const path = t('urls.terms_and_conditions')
    if (typeof path !== 'string' || !path) {
      return '#'
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path
    }
    const relativePath = path.startsWith('/') ? path : `/${path}`
    if (isNativeMobile.value) {
      return `https://yumnia.com${relativePath}`
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${relativePath}`
    }
    return `https://yumnia.com${relativePath}`
  })

  async function openUpgradeTerms (event) {
    if (!isNativeMobile.value) return
    event.preventDefault()
    const url = upgradeTermsUrl.value
    if (!url || url === '#') return
    try {
      await Browser.open({ url })
    } catch {
      // WebView may block navigation; Browser opens in-app / SFSafariViewController.
    }
  }

  async function openExternalUrl (url) {
    if (!url) return
    if (isNativeMobile.value) {
      try {
        await Browser.open({ url })
        return
      } catch {
        // fall through
      }
    }
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  async function goToCheckout () {
    const ctaId = auth.isAuthenticated
      ? 'upgrade_page_checkout_signed_in'
      : 'upgrade_page_checkout_guest'
    let cancelTracked = false
    const trackCheckoutCanceled = async ({
      checkoutProvider,
      cancelStage,
      errorCode = null,
    }) => {
      if (cancelTracked) return
      cancelTracked = true
      await consentStore.trackEvent('checkout_canceled', {
        source,
        checkout_provider: checkoutProvider,
        platform: isNativeMobile.value ? Capacitor.getPlatform() : 'web',
        cta_id: ctaId,
        plan: selectedPlan.value,
        cancel_stage: cancelStage,
        error_code: errorCode,
      })
    }
    // `action` rather than a lookup table of surface names. The same event name
    // is fired when a modal opens and when checkout opens, and reading one as
    // the other overstates intent by every visitor who only looked at prices.
    await consentStore.trackEvent('upgrade_cta_clicked', {
      action: 'open_checkout',
      surface: source,
      cta_id: ctaId,
      plan: selectedPlan.value,
    })
    checkoutLoading.value = true
    errorMessage.value = ''
    syncPending.value = false
    purchaseSuccessVisible.value = false
    try {
      if (isNativeMobile.value) {
        if (!auth.isAuthenticated || !auth.user?.id) {
          errorMessage.value = t('planner.upgrade_page_sign_in_required_mobile')
          return
        }

        const configured = await initializeRevenueCat()
        if (!configured) {
          errorMessage.value = t('planner.upgrade_page_purchase_error')
          return
        }
        try {
          await loginRevenueCatUser(auth.user.id)
        } catch (loginError) {
          const msg = getRevenueCatPurchaseErrorMessage(loginError, t)
          errorMessage.value = msg || t('planner.upgrade_page_purchase_error')
          return
        }

        const pkg = selectedPlan.value === 'annual'
          ? nativePackages.value.annual
          : nativePackages.value.monthly
        if (!pkg) {
          errorMessage.value = t('planner.upgrade_page_purchase_package_unavailable')
          return
        }

        await consentStore.trackEvent('revenuecat_purchase_opened', {
          source,
          checkout_provider: 'revenuecat',
          platform: Capacitor.getPlatform(),
          cta_id: ctaId,
          plan: selectedPlan.value,
        })

        try {
          const purchaseResult = await purchaseRevenueCatPackage(pkg)
          nativePurchaseWasTrial.value = purchaseStartedFreeTrial(purchaseResult)
        } catch (purchaseError) {
          if (isRevenueCatPurchaseUserCancelled(purchaseError)) {
            const rawCode = purchaseError && typeof purchaseError === 'object'
              ? purchaseError.code
              : null
            await trackCheckoutCanceled({
              checkoutProvider: 'revenuecat',
              cancelStage: 'purchase_sheet',
              errorCode: rawCode !== undefined && rawCode !== null ? String(rawCode) : null,
            })
            return
          }
          errorMessage.value = getRevenueCatPurchaseErrorMessage(purchaseError, t)
          return
        }

        await consentStore.trackEvent('purchase_completed', {
          product_id: pkg?.product?.identifier ?? null,
          plan: selectedPlan.value,
          platform: Capacitor.getPlatform(),
          checkout_provider: 'revenuecat',
          ...gaDimensionsFromStoredUtm(),
        })

        const syncResult = await syncPremiumWithBackend()
        applySyncResult(syncResult)
        return
      }

      const shownTerms = pricingFingerprint(webPricing.value)
      await loadWebPricing({ force: true })
      if (pricingStatus.value !== 'ready' || !webPricing.value || pricingFingerprint(webPricing.value) !== shownTerms) {
        errorMessage.value = t('planner.upgrade_page_pricing_changed')
        return
      }

      const checkoutPlan = webCheckoutPlan(webPricing.value, selectedPlan.value)
      const { data } = await axios.post('/api/checkout-session', { plan: checkoutPlan })
      const url = data?.url
      if (!url) {
        errorMessage.value = t('planner.upgrade_page_checkout_error')
        return
      }

      await consentStore.trackEvent('stripe_checkout_opened', {
        source,
        checkout_provider: 'stripe',
        open_target: 'same_tab',
        cta_id: ctaId,
        plan: selectedPlan.value,
        price_tier: typeof data?.price_tier === 'string' ? data.price_tier : selectedPriceTier.value,
      })

      window.location.assign(url)
    } catch (error) {
      const errorCode = error?.response?.data?.error
      if (errorCode === 'already_premium') {
        errorMessage.value = auth.user?.premium_type === 'family_premium'
          ? t('planner.upgrade_family_premium_error')
          : t('planner.upgrade_already_premium_error')
        return
      }
      if (!isNativeMobile.value && errorCode === 'founding_offer_unavailable') {
        webPricing.value = {
          ...webPricing.value,
          founding_offer: {
            ...webPricing.value.founding_offer,
            active: false,
            seats_left: null,
            prices: null,
          },
        }
        void retryWebPricing()
        errorMessage.value = t('planner.upgrade_page_founding_offer_unavailable')
        return
      }
      if (isNativeMobile.value && isRevenueCatPurchaseUserCancelled(error)) {
        const rawCode = error && typeof error === 'object'
          ? error.code
          : null
        await trackCheckoutCanceled({
          checkoutProvider: 'revenuecat',
          cancelStage: 'purchase_sheet',
          errorCode: rawCode !== undefined && rawCode !== null ? String(rawCode) : null,
        })
        return
      }
      if (!isNativeMobile.value) {
        errorMessage.value = getApiErrorMessage(error, t, 'planner.upgrade_page_checkout_error')
      } else {
        errorMessage.value = getRevenueCatPurchaseErrorMessage(error, t) || t('planner.upgrade_page_purchase_error')
      }
    } finally {
      checkoutLoading.value = false
    }
  }

  return {
    selectedPlan,
    isNativeMobile,
    isIosPlatform,
    checkoutLoading,
    errorMessage,
    syncPending,
    syncRetryLoading,
    purchaseSuccessVisible,
    purchaseSuccessIsTrial,
    purchaseSuccessMessage,
    purchaseSuccessTitle,
    firstName,
    trialDays,
    trialMode,
    hasTrial,
    foundingSeatsLeft,
    foundingSeatCap,
    foundingSavingsInfo,
    webPricingReady,
    webDisclosure,
    webPricingLoading: showWebPricingSkeleton,
    webPricingFailed: showWebPricingError,
    retryWebPricing,
    webMonthlySavingDisplay,
    nativePackages,
    nativePricingError,
    nativePricingRetryLoading,
    nativePlansReady,
    nativeMonthlyPriceDisplay,
    nativeAnnualPriceDisplay,
    nativeAnnualMonthlyEquivalentDisplay,
    nativeSavingsPercent,
    webSelectedPlanPriceLabel,
    webFoundingOfferActive,
    webMonthlyPriceDisplay,
    webAnnualPriceDisplay,
    webAnnualBilledDisplay,
    webAnnualPerMonthDisplay,
    webAnnualSavingDisplay,
    webStandardMonthlyPriceDisplay,
    webStandardAnnualPriceDisplay,
    annualTrialLine,
    monthlyTrialLine,
    showNativeTrialNote,
    checkoutDisabled,
    ctaLabel,
    loadNativeOfferings,
    retryLoadNativeOfferings,
    retryRevenueCatSync,
    goToCheckout,
    upgradeTermsUrl,
    openUpgradeTerms,
    openExternalUrl,
  }
}
